import { Moment } from 'moment/moment';
import {
  EManifestStatuses,
  EOrderStatuses,
  EOrderTypes,
} from 'app/consts/enums';
import { timeStamp } from '../../models/interfaces';

export interface IUnassignedOrder {
  orderId: number;
  orderTypeId: EOrderTypes;
  orderTypeName: string;
  orderStatusId: EOrderStatuses;
  orderStatusName: string;

  deliveryAddress: string | null;
  deliveryClientId: number | null;
  deliveryClientName: string;
  deliveryLat: number | null;
  deliveryLong: number | null;
  deliveryTime: string;
  deliveryTolerance: number;
  deliveryServiceTime: number;

  pickupAddress: string | null;
  pickupClientId: number | null;
  pickupClientName: string;
  pickupLat: number | null;
  pickupLong: number | null;
  pickupTime: string;
  pickupTolerance: number;
  pickupServiceTime: number;

  isPickup: 0 | 1;
  onTime: -1 | 0 | 1; // -1 - earlier; 0 - on time; 1 - later
  IsFutile: boolean;

  customerReference: string | null;
  capacityUnits: string;
  capacityName: string;
  orderTotalCapacity: number | null;
  orderMaxDimension: number | null;
  attributes: number[] | null;
  length: number | null;
  price: number | null;
  volume: number | null;
  planningUnAssignedStatus?: ILiveMapPlanningManifesUnassignedOrderStatus;
  planningOriginalManifestId?: number;
  totalItems: number;
  totalItemPrice: number;
  totalItemWeight: number;
  totalItemVolume: number;
  unitsInOrder: ILiveMapOrderUnit[];

  id?: number;
  shipmentId?: number;
  breakTimeId?: number;
  numberOfOrder?: number;
  isCollapsed?: boolean;
}

export interface ILiveMapManifestOrder {
  orderId: number;
  orderTypeId: EOrderTypes;
  orderTypeName: string;
  orderStatusId: EOrderStatuses;
  orderStatusName: string;

  deliveryAddress: string | null;
  deliveryClientId: number | null;
  deliveryClientName: string;
  deliveryLat: number | null;
  deliveryLong: number | null;
  deliveryTime: string;
  deliveryTolerance: number;

  pickupAddress: string | null;
  pickupClientId: number | null;
  pickupClientName: string;
  pickupLat: number | null;
  pickupLong: number | null;
  pickupTime: string;
  pickupTolerance: number;

  customerReference: string | null;
  capacityUnits: string;
  capacityName: string;
  orderTotalCapacity: number | null;
  orderMaxDimension: number | null;
  attributes: number[] | null;
  length: number | null;
  price: number | null;
  volume: number | null;

  deliveryServiceTime: number;
  pickupServiceTime: number;
  deliveryComment: string;
  pickupComment: string;
  startTime: timeStamp;
  travelTime: number; // in seconds
  serviceTime: number; // in seconds
  linkId: number;
  isPickup: 0 | 1;
  IsFutile: boolean;
  onTime: -1 | 0 | 1; // -1 - earlier; 0 - on time; 1 - later
  earliestArrival: timeStamp;
  planningStatus?: ILiveMapPlanningManifestOrderStatus;
  planningOriginalManifestId?: number;
  isLocked?: boolean; // Indicates if the order is locked for editing
  totalItems: number;
  totalItemPrice: number;
  totalItemWeight: number;
  totalItemVolume: number;
  unitsInOrder: ILiveMapOrderUnit[];
  totalCapacity?: number;
  isHighlighted: boolean;
  breakTimeId?: number;
  shipmentId?: number;
  numberOfOrder?: number;

  sequenceNumber?: number;
  isCollapsed?: boolean;
}

export interface ILiveMapOrderUnit {
  stat_id: number;
  id: number;
  name: string;
  type: string;
  qty: number;
}

export interface ILiveMapPlanningManifestOrder extends ILiveMapManifestOrder {
  planningOrderStatus: ILiveMapPlanningManifestOrderStatus;
}

export type LiveMapOrder = IUnassignedOrder | ILiveMapManifestOrder;

export interface ILiveMapManifest {
  id: number;
  name: string;
  driverId: number;
  driverFullName: string;
  vehicleId: number;
  vehicleName: string;
  date: timeStamp;
  orders: ILiveMapManifestOrder[];
  startLat: number | null;
  startLng: number | null;
  endLat: number | null;
  endLng: number | null;
  routeDistance: number | null;
  routeMinutes: number | null;
  isActive: boolean;
  status: EManifestStatuses;
  manifestDefaultStart: timeStamp;
  manifestDefaultStop: timeStamp;
  earliestStart: timeStamp;
  latestFinish: timeStamp;
  vehicleCapacity?: number;
  totalCapacity?: number;
  currentLoadCapacity?: number;
  capacityUnits?: string;
  vehicleMaxLength?: number | null;
  isFiltered?: boolean;
  isSelected?: boolean;
  ordersCompletedOrCancelledCount: number;
  ordersInProgressCount: number;
  isCompleted?: boolean;
  isConfirmed?: boolean;
}

export interface IPlanningManifest extends ILiveMapManifest {
  position?: number;
  savingStatus?: IPlanningSaveStatusEnum;
  planningStatus?: ILiveMapPlanningManifestStatus;
  errors?: string[];
  isNewOrderAllocated?: boolean;
}

export interface IManifestFilter {
  id: number;
  name: string;
  isSelected: boolean;
  date: Moment;
}

export interface ILiveMapManifestCreate {
  name: string;
  driverId: number;
  vehicleId: number;
  date?: timeStamp;
  orderIds: number[];
  earliestStart?: string;
  latestFinish?: string;
}

export enum ILiveMapPlanningManifestStatus {
  New = 1,
  Existing = 2,
  Untouched = 3,
}

export enum ILiveMapPlanningManifestOrderStatus {
  New = 1,
  Moved = 2,
  Untouched = 3,
  Removed = 4,
}

export enum ILiveMapPlanningManifesUnassignedOrderStatus {
  Unassigned = 1,
  Assigned = 2,
  Unlinked = 3,
}

export type OrdersFilter = Record<EOrderTypes, boolean>;
export type ManifestsFilter = Record<EManifestStatuses, boolean>;

export interface ILiveMapFilter {
  orders?: OrdersFilter;
  manifests?: ManifestsFilter;
  date: Moment;
  showOrdersWithoutDate: boolean;
  availableManifests?: IManifestFilter[];
  manifestStatusFilters?: BaseDropdownFilters[];
  orderTypeFilter?: BaseDropdownFilters[];
  manifestName?: string;
  orderTextFilter?: string;
  isSortingByManifestNameAsc?: boolean;
  highlightedOrderId?: number | null;
}

export interface ILiveMapFilterStorage {
  orders?: OrdersFilter;
  manifests?: ManifestsFilter;
  date: string;
  showOrdersWithoutDate: boolean;
}

export interface ILiveMapFilterEvent {
  current: ILiveMapFilter;
  previous: ILiveMapFilter;
}

export interface ILiveMapManifestToReordering {
  id: number | null;
  orders: ILiveMapManifestReorder[];
  name?: string;
  manifestId?: number;
  driverId?: number;
  vehicleId?: number;
  earliestStart?: string;
  latestFinish?: string;
  manifestDate?: string;
}
export interface ILiveMapManifestReorder {
  orderId?: number;
  linkId?: number;
  breakTimeId?: number;
  isLocked?: boolean;
}

export interface ILiveMapManifestUpdate {
  Manifests: {
    ManifestId: number;
    ManifestItems: { LinkId?: number; OrderId?: number }[];
  }[];
}

export interface IUnassignedOrdersSelector {
  selectedOrderIds: number[];
  ordersIdsToSaving: number[];
  isDragging: boolean;
}

export interface ILiveMapVehicleGetDTO {
  Id: number;
  Name: string;
  Lat: number;
  Lng: number;
  LastOnline: timeStamp;
}

export interface ILiveMapVehicle {
  id: number;
  name: string;
  lat: number;
  lng: number;
  lastOnline: timeStamp;
  assignedManifests: { id: number; name: string }[];
}

export enum EVehicleEnrouteStatus {
  None = 'none',
  Enroute = 'enroute',
}

export enum EVehicleOnlineStatus {
  Online = 'online',
  Offline = 'offline',
}

export interface UnlinkOrderResult {
  unlinkedOrder: boolean;
  removedManifest: boolean;
}

export interface ISummaryCounter {
  orders: {
    pending: number;
    enroute: number;
    completed: number;
    cancelled: number;
    delayed: number;
  };
  manifests: {
    completed: number;
  };
  drivers: {
    lessThanFiveMins: number;
    lessThanEightHours: number;
    moreThanEightHours: number;
  };
}

export enum PlanningModeOrderActionType {
  None = 0,
  Add = 1,
  Remove = 2,
  Move = 3,
}
export interface PlanningModeOrderChangesParameter {
  newManifest: ILiveMapManifest;
  action: PlanningModeOrderActionType;
  orders: ILiveMapManifestOrder[];
  fromManitestId?: number;
}

export interface IManifestEditData {
  manifestName: string;
  manifestDate: string;
  selectedDriverID: number;
  selectedVehicleID: number;
  EarliestStart?: string;
  LatestFinish?: string;
  selectedDriverFullName?: string;
  selectedVehicleName?: string;
  driverTimetable?: string;
  isDriverAndVehicleDisabled?: boolean;
  isManifestCompleted?: boolean;
  id: number;
  vehicleCapacity?: number;
}

export interface IPlanningSaveResponse {
  isManifestCreationSuccessful: boolean;
  errors: string[];
  manifestId: number;
  manifest?: ILiveMapManifest;
}

export enum IPlanningSaveStatusEnum {
  Ready = 0,
  Processing = 1,
  Successful = 2,
  Failed = 3,
  NoChange = 4,
}

export enum IListModePopupSourceEnum {
  ViewSummary = 1,
  Save = 2,
  Optimised = 3,
}

export interface IListModeSummaryProps {
  Source: IListModePopupSourceEnum;
  SelectedManifest?: IPlanningManifest;
}

export interface IListModeSummaryResult {
  NeedRefresh: boolean;
  SelectedManifest?: IPlanningManifest;
}

export interface BaseDropdownFilters {
  id: number;
  description: string;
  isSelected: boolean;
}

export interface IMapEventProps {
  manifestId: number;
  orderId: number | null;
}
